import { useEffect, useCallback } from "react";

import { useRoofingEstimator, useCart, useSupplier } from "../../../../hooks/query";
import { useProject } from "../../../../hooks";
import { MATERIAL_DEPENDENCIES } from "./utils/materialDependencies";

interface UseMaterialsMasterDataInitProps {
    measurements: any;
    // States
    materialSchema: any[];
    // Setters
    setMaterialSchema: (schema: any[]) => void;
    setCart: (cart: any[]) => void;
    setPriceRanges: (ranges: Record<string, { min: number; max: number }>) => void;
    setIdentifiedProducts: (products: any) => void;
    setMissingProducts: (products: any[]) => void;
    setIsLoading: (isLoading: boolean) => void;
    setSelectedValues: (values: Record<string, any>) => void;
}

/**
 * Initialization Hook: Loads data from API and hydrates the local UI state.
 */
export default function useMaterialsMasterDataInit(stateSetters: UseMaterialsMasterDataInitProps) {
    const {
        measurements,
        setMaterialSchema,
        setCart,
        setPriceRanges,
        setIdentifiedProducts,
        setMissingProducts,
        setIsLoading,
        setSelectedValues,
    } = stateSetters;

    const { project } = useProject();
    const { useIdentifyProducts } = useRoofingEstimator();
    const { useListByProjectId } = useCart();
    const { useListPriceRanges } = useSupplier();

    const identifiedProductsSvc = useIdentifyProducts();
    const cartListSvc = useListByProjectId();
    const listPriceRangesSvc = useListPriceRanges();

    const fetchCartItems = useCallback(async () => {
        if (!project?.id) return [];
        return await cartListSvc.mutateAsync({ ProjectId: project.id });
    }, [project?.id, cartListSvc]);

    const getRequiredMaterials = async () => {
        try {
            const params = Object.fromEntries(
                measurements.map(({ MeasurementType, ActualValue, CalculatedValue }: any) => [
                    MeasurementType,
                    ActualValue ?? CalculatedValue,
                ]),
            );

            return await identifiedProductsSvc.mutateAsync(params);
        } catch (error) {
            console.error("Error in getRequiredMaterials:", error);
            return [];
        }
    };

    const getSchemaWithDependencies = (requiredMaterials: any[], allAvailableMaterials: any[]) => {
        const allAvailableWithMeta = allAvailableMaterials.map((item: any) => ({
            ...item,
            name: item.ProductName ?? item.Name,
            Name: item.ProductName ?? item.Name,
        }));

        const requiredMaterialsWithMeta = requiredMaterials
            .map(({ ProductName }: any) => {
                const matched = allAvailableWithMeta.find((item: any) => item.name === ProductName);
                return matched || null;
            })
            .filter(Boolean);

        const productSelectionMetaData = requiredMaterialsWithMeta.reduce((acc: any, curr: any) => {
            const name = curr.name;

            if (!acc[name]) {
                acc[name] = { ...curr };
            }

            const dependency = MATERIAL_DEPENDENCIES.find((depProd: any) => depProd.Category === name);
            if (!dependency) {
                return acc;
            }

            for (const requiredCategory of dependency.RequiresCategories) {
                const existingDependency = acc[requiredCategory];

                if (existingDependency) {
                    acc[requiredCategory] = {
                        ...existingDependency,
                        DependentsOn: [...(existingDependency.DependentsOn || []), name],
                    };
                } else {
                    const productTypeMeta = allAvailableWithMeta.find((e: any) => e.name === requiredCategory);
                    if (productTypeMeta) {
                        acc[requiredCategory] = {
                            ...productTypeMeta,
                            name: requiredCategory,
                            Name: requiredCategory,
                            DependentsOn: [name],
                        };
                    }
                }
            }

            return acc;
        }, {});

        return Object.values(productSelectionMetaData);
    };

    const calculatePriceRanges = useCallback(
        async (schema: any[]) => {
            if (schema.length === 0) return { ranges: {}, raw: [] };

            try {
                const apiRanges = await listPriceRangesSvc.mutateAsync();
                const ranges: Record<string, { min: number; max: number }> = {};

                if (Array.isArray(apiRanges)) {
                    apiRanges.forEach((item: any) => {
                        const min = Number(item.MinPrice);
                        const max = Number(item.MaxPrice);
                        ranges[item.ProductName] = { min, max };

                        const category = item.CategoryName;
                        if (!ranges[category]) {
                            ranges[category] = { min, max };
                        } else {
                            ranges[category].min = Math.min(ranges[category].min, min);
                            ranges[category].max = Math.max(ranges[category].max, max);
                        }
                    });
                }

                setPriceRanges(ranges);
                return { ranges, raw: apiRanges };
            } catch (error) {
                console.error("Error calculating price ranges:", error);
                return { ranges: {}, raw: [] };
            }
        },
        [listPriceRangesSvc, setPriceRanges],
    );

    const loadMasterData = useCallback(async () => {
        if (!project?.id) {
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        try {
            const requiredMaterials = await getRequiredMaterials();
            if (!requiredMaterials || !Array.isArray(requiredMaterials)) {
                setIsLoading(false);
                return;
            }

            const [cartItems, { raw: apiRanges }] = await Promise.all([
                fetchCartItems(),
                calculatePriceRanges(requiredMaterials),
            ]);

            const initialSchema = getSchemaWithDependencies(requiredMaterials, requiredMaterials);

            const finalMaterialSchema = initialSchema.map((category: any) => {
                const subcategories = category.Subcategories || [];
                if (subcategories.length > 0) {
                    const enrichedSubs = subcategories.map((sub: any) => {
                        const normalizedName = (sub.Name || "").toLowerCase().trim();
                        const supplierMatch = apiRanges.find((p: any) => {
                            const pName = (p.ProductName || "").toLowerCase().trim();
                            return (
                                pName === normalizedName ||
                                pName.includes(normalizedName) ||
                                normalizedName.includes(pName)
                            );
                        });

                        return {
                            ...sub,
                            Id: supplierMatch?.Id || sub.Id,
                            Color: supplierMatch?.Color || sub.Color,
                        };
                    });

                    return { ...category, Subcategories: enrichedSubs };
                }

                const targetName = (category.Name || "").toLowerCase().trim();
                const productsInCategory = apiRanges
                    .filter((p: any) => (p.CategoryName || "").toLowerCase().trim() === targetName)
                    .map((p: any) => ({
                        Id: p.Id,
                        Name: p.ProductName,
                        ProductName: p.ProductName,
                        Color: p.Color,
                    }));

                return { ...category, Subcategories: productsInCategory };
            });

            // Hydration Logic
            const hydratedSelections: Record<string, any> = {};
            cartItems.forEach((item: any) => {
                if (item.ProductCategory) {
                    hydratedSelections[item.ProductCategory] = {
                        id: item.Id,
                        value: item.ProductName,
                        quantity: item.ItemCount,
                        color: item.Color,
                        manufacturerProductId: item.ManufacturerProductId,
                        supplierId: item.SupplierId,
                        supplierName: item.SupplierName,
                        unitPrice: item.UnitPrice,
                    };
                }
            });
            setSelectedValues(hydratedSelections);

            setIdentifiedProducts(requiredMaterials);
            setCart(cartItems);
            setMaterialSchema(finalMaterialSchema);

            const missing = requiredMaterials
                .filter((item: any) => {
                    const normalizedItem = (item.ProductName || "").toLowerCase().trim();
                    const isAvailable = apiRanges.some(
                        (p: any) =>
                            (p.ProductName || "").toLowerCase().trim() === normalizedItem ||
                            (p.CategoryName || "").toLowerCase().trim() === normalizedItem,
                    );
                    return !isAvailable;
                })
                .map((item: any) => item.ProductName);

            setMissingProducts(missing);
        } catch (error) {
            console.error("Error loading materials data:", error);
        } finally {
            setIsLoading(false);
        }
    }, [
        project?.id,
        measurements,
        setIsLoading,
        setMissingProducts,
        fetchCartItems,
        calculatePriceRanges,
        setIdentifiedProducts,
        setCart,
        setMaterialSchema,
        setSelectedValues,
    ]);

    useEffect(() => {
        if (Array.isArray(measurements) && measurements.length > 0) {
            loadMasterData();
        }
    }, [measurements]);

    return {
        loadMasterData,
        fetchCartItems,
        calculatePriceRanges,
    };
}
